import * as React from 'react';
import { CheckCircle2, FileSpreadsheet, RefreshCw, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ImportDetail } from '@klareospace/pharos-lib-types';
import { IMPORT_KIND } from '@klareospace/pharos-lib-types';
import { cn, MetricTile } from '@klareospace/pharos-lib-web-utils';

import { countRejected } from '../utils';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ImportReportMetricsProps {
  importDoc: ImportDetail;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * KPI row for the import report — Rows imported / Created / Updated / Rejected. The "Updated" tile
 * is hidden for handling-units imports: the backend always counts an applied HU line as `created`
 * (`linesCount.updated` is always 0 for this kind), so the tile would be permanently meaningless.
 */
export function ImportReportMetrics({ importDoc }: ImportReportMetricsProps): React.JSX.Element {
  const { t } = useTranslation('imports');
  const showUpdatedMetric = importDoc.kind !== IMPORT_KIND.HANDLING_UNITS;

  return (
    <div className={cn('grid grid-cols-2 gap-3', showUpdatedMetric ? 'md:grid-cols-4' : 'md:grid-cols-3')}>
      <MetricTile
        icon={<FileSpreadsheet size={18} />}
        label={t('report.metrics.rowsImported')}
        value={importDoc.linesCount.total}
        variant="neutral"
      />
      <MetricTile
        icon={<CheckCircle2 size={18} />}
        label={t('report.metrics.created')}
        value={importDoc.linesCount.created}
        variant="success"
      />
      {showUpdatedMetric && (
        <MetricTile
          icon={<RefreshCw size={18} />}
          label={t('report.metrics.updated')}
          value={importDoc.linesCount.updated}
          variant="info"
        />
      )}
      <MetricTile
        icon={<XCircle size={18} />}
        label={t('report.metrics.rejected')}
        value={countRejected(importDoc)}
        variant="danger"
      />
    </div>
  );
}
